import { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  MessageSquare,
  Wrench,
  Database,
  GitBranch,
  Sparkles,
  Save,
  Trash2,
  Maximize2,
  Minimize2,
  Keyboard,
} from 'lucide-react';
import { nodeTypes } from './FlowNodes';
import api from '../../services/api';

interface FlowPromptBuilderProps {
  onSave?: (optimizedPrompt: string, flowData: any) => void;
  initialFlow?: { nodes: Node[]; edges: Edge[] };
}

const initialNodes: Node[] = [
  {
    id: 'start-1',
    type: 'start',
    position: { x: 250, y: 50 },
    data: { label: 'Inicio' },
  },
];

export const FlowPromptBuilder: React.FC<FlowPromptBuilderProps> = ({
  onSave,
  initialFlow,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialFlow?.nodes || initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialFlow?.edges || []);
  const [tools, setTools] = useState<any[]>([]);
  const [resources, setResources] = useState<any[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedPrompt, setOptimizedPrompt] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Cargar tools y recursos disponibles
  useEffect(() => {
    loadToolsAndResources();
  }, []);

  // Manejar tecla ESC para salir de pantalla completa y shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      // ESC: Salir de pantalla completa o deseleccionar nodo
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else if (selectedNode) {
          setSelectedNode(null);
        }
        return;
      }

      // Ctrl/Cmd + S: Guardar prompt (si está optimizado)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (optimizedPrompt && onSave) {
          handleSave();
        }
        return;
      }

      // Ctrl/Cmd + O: Optimizar con AI
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        if (nodes.length > 1 && !isOptimizing) {
          optimizePrompt();
        }
        return;
      }

      // Ctrl/Cmd + I: Agregar instrucción
      if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
        e.preventDefault();
        addInstructionNode();
        return;
      }

      // Ctrl/Cmd + K: Agregar condición
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        addConditionNode();
        return;
      }

      // Delete o Backspace: Eliminar nodo seleccionado
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode && selectedNode.id !== 'start-1') {
        e.preventDefault();
        deleteSelectedNode();
        return;
      }

      // F11: Toggle pantalla completa
      if (e.key === 'F11') {
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [isFullscreen, selectedNode, nodes, isOptimizing, optimizedPrompt]);

  const loadToolsAndResources = async () => {
    try {
      const [toolsRes, resourcesRes] = await Promise.all([
        api.tools.list(),
        api.resources.list(),
      ]);
      setTools(toolsRes.tools || []);
      setResources(resourcesRes.resources || []);
    } catch (error) {
      console.error('Error loading tools and resources:', error);
    }
  };

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Agregar nodo de instrucción
  const addInstructionNode = () => {
    const newNode: Node = {
      id: `instruction-${Date.now()}`,
      type: 'instruction',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 200 },
      data: { label: 'Instrucción', instruction: '' },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  // Agregar nodo de tool
  const addToolNode = (tool: any) => {
    const newNode: Node = {
      id: `tool-${Date.now()}`,
      type: 'tool',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 200 },
      data: {
        label: 'Tool',
        toolId: tool.id,
        toolName: tool.name,
        toolDescription: tool.description,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  // Agregar nodo de recurso
  const addResourceNode = (resource: any) => {
    const newNode: Node = {
      id: `resource-${Date.now()}`,
      type: 'resource',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 200 },
      data: {
        label: 'Recurso',
        resourceId: resource.id,
        resourceName: resource.name,
        resourceType: resource.resource_type,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  // Agregar nodo de condición
  const addConditionNode = () => {
    const newNode: Node = {
      id: `condition-${Date.now()}`,
      type: 'condition',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 200 },
      data: { label: 'Condición', condition: '' },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  // Eliminar nodo seleccionado
  const deleteSelectedNode = () => {
    if (selectedNode && selectedNode.id !== 'start-1') {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
      setSelectedNode(null);
    }
  };

  // Actualizar datos del nodo seleccionado
  const updateNodeData = (nodeId: string, newData: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...newData } };
        }
        return node;
      })
    );
  };

  // Validar flujo antes de optimizar
  const validateFlow = () => {
    const errors: string[] = [];

    // Verificar que haya al menos un nodo además del start
    if (nodes.length <= 1) {
      errors.push('Debes agregar al menos un nodo al flujo');
    }

    // Verificar que haya conexiones
    if (edges.length === 0 && nodes.length > 1) {
      errors.push('Debes conectar los nodos del flujo');
    }

    // Verificar que los nodos de instrucción tengan contenido
    const emptyInstructions = nodes.filter(
      n => n.type === 'instruction' && !(n.data as any).instruction?.trim()
    );
    if (emptyInstructions.length > 0) {
      errors.push(`${emptyInstructions.length} nodo(s) de instrucción están vacíos`);
    }

    // Verificar que los nodos de condición tengan contenido
    const emptyConditions = nodes.filter(
      n => n.type === 'condition' && !(n.data as any).condition?.trim()
    );
    if (emptyConditions.length > 0) {
      errors.push(`${emptyConditions.length} nodo(s) de condición están vacíos`);
    }

    // Verificar que el nodo start tenga conexión de salida
    const startNode = nodes.find(n => n.type === 'start');
    if (startNode && !edges.some(e => e.source === startNode.id)) {
      errors.push('El nodo de inicio debe estar conectado a otro nodo');
    }

    return errors;
  };

  // Optimizar prompt con AI
  const optimizePrompt = async () => {
    // Validar flujo primero
    const validationErrors = validateFlow();
    if (validationErrors.length > 0) {
      alert('Errores en el flujo:\n\n' + validationErrors.join('\n'));
      return;
    }

    setIsOptimizing(true);
    try {
      // Convertir flujo a estructura para AI
      const flowStructure = {
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type,
          data: n.data,
        })),
        edges: edges.map((e) => ({
          source: e.source,
          target: e.target,
        })),
      };

      // Llamar a la API para optimizar
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'https://mcp-hub-universal.rckflr.workers.dev';

      const res = await fetch(`${API_URL}/api/prompts/optimize-flow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ flow: flowStructure }),
      });

      if (!res.ok) {
        throw new Error('Failed to optimize prompt');
      }

      const response = await res.json();
      setOptimizedPrompt(response.optimized_prompt);
    } catch (error) {
      console.error('Error optimizing prompt:', error);
      alert('Error al optimizar el prompt');
    } finally {
      setIsOptimizing(false);
    }
  };

  // Guardar prompt
  const handleSave = () => {
    if (onSave && optimizedPrompt) {
      onSave(optimizedPrompt, { nodes, edges });
    }
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'} flex flex-col bg-gray-50`}>
      {/* Toolbar superior */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Editor Visual de Prompts</h2>
            <div className="flex gap-2">
              <button
                onClick={addInstructionNode}
                className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <MessageSquare size={16} />
                Instrucción
              </button>
              <button
                onClick={addConditionNode}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
              >
                <GitBranch size={16} />
                Condición
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShortcuts(!showShortcuts)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              title="Ver atajos de teclado"
            >
              <Keyboard size={16} />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {isFullscreen ? 'Salir' : 'Pantalla Completa'}
            </button>
            <button
              onClick={optimizePrompt}
              disabled={isOptimizing || nodes.length <= 1}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 transition-colors text-sm"
            >
              <Sparkles size={16} />
              {isOptimizing ? 'Optimizando...' : 'Optimizar con AI'}
            </button>
            {optimizedPrompt && (
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                <Save size={16} />
                Guardar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Layout principal */}
      <div className="flex-1 flex">
        {/* Sidebar con tools y recursos */}
        <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
          <div className="space-y-6">
            {/* Tools */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Wrench size={16} className="text-purple-600" />
                Tools Disponibles
              </h3>
              <div className="space-y-2">
                {tools.length === 0 ? (
                  <p className="text-xs text-gray-500">No hay tools disponibles</p>
                ) : (
                  tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => addToolNode(tool)}
                      className="w-full text-left p-2 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors border border-purple-200"
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{tool.name}</div>
                      {tool.description && (
                        <div className="text-xs text-gray-600 line-clamp-2 mt-1">
                          {tool.description}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Database size={16} className="text-green-600" />
                Recursos RAG
              </h3>
              <div className="space-y-2">
                {resources.length === 0 ? (
                  <p className="text-xs text-gray-500">No hay recursos disponibles</p>
                ) : (
                  resources.map((resource) => (
                    <button
                      key={resource.id}
                      onClick={() => addResourceNode(resource)}
                      className="w-full text-left p-2 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200"
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {resource.name}
                      </div>
                      <div className="text-xs text-gray-500 uppercase mt-1">
                        {resource.resource_type}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Panel de edición del nodo seleccionado */}
        {selectedNode && selectedNode.id !== 'start-1' && (
          <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Editar Nodo</h3>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Editar Instruction Node */}
              {selectedNode.type === 'instruction' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instrucción
                  </label>
                  <textarea
                    value={String((selectedNode.data as any).instruction || '')}
                    onChange={(e) =>
                      updateNodeData(selectedNode.id, { instruction: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={6}
                    placeholder="Escribe la instrucción aquí..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Describe qué debe hacer el agente en este paso
                  </p>
                </div>
              )}

              {/* Editar Condition Node */}
              {selectedNode.type === 'condition' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Condición
                  </label>
                  <textarea
                    value={String((selectedNode.data as any).condition || '')}
                    onChange={(e) =>
                      updateNodeData(selectedNode.id, { condition: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    rows={4}
                    placeholder="Ej: Si el resultado es mayor a 100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Define la condición para bifurcar el flujo
                  </p>
                </div>
              )}

              {/* Info de Tool Node (solo lectura) */}
              {selectedNode.type === 'tool' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tool
                    </label>
                    <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="font-medium text-gray-900">
                        {String((selectedNode.data as any).toolName || '')}
                      </div>
                      {(selectedNode.data as any).toolDescription && (
                        <div className="text-xs text-gray-600 mt-1">
                          {String((selectedNode.data as any).toolDescription)}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Este tool se ejecutará en este punto del flujo
                  </p>
                </div>
              )}

              {/* Info de Resource Node (solo lectura) */}
              {selectedNode.type === 'resource' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recurso
                    </label>
                    <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="font-medium text-gray-900">
                        {String((selectedNode.data as any).resourceName || '')}
                      </div>
                      <div className="text-xs text-gray-500 uppercase mt-1">
                        {String((selectedNode.data as any).resourceType || '')}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Este recurso se consultará usando RAG en este punto
                  </p>
                </div>
              )}

              {/* Botón eliminar */}
              <button
                onClick={deleteSelectedNode}
                className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
              >
                <Trash2 size={16} />
                Eliminar Nodo
              </button>
            </div>
          </div>
        )}

        {/* Canvas de ReactFlow */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelectedNode(node)}
            onPaneClick={() => setSelectedNode(null)}
            nodeTypes={nodeTypes}
            fitView
            className="bg-gray-50"
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>

          {/* Panel de prompt optimizado */}
          {optimizedPrompt && (
            <div className="absolute bottom-4 right-4 w-96 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-600" />
                  Prompt Optimizado
                </h3>
                <button
                  onClick={() => setOptimizedPrompt('')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded border border-gray-200">
                {optimizedPrompt}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Shortcuts */}
      {showShortcuts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Keyboard size={24} className="text-blue-600" />
                  Atajos de Teclado
                </h2>
                <button
                  onClick={() => setShowShortcuts(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Navegación */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Navegación</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Salir de pantalla completa / Deseleccionar</span>
                      <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">ESC</kbd>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Pantalla completa</span>
                      <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">F11</kbd>
                    </div>
                  </div>
                </div>

                {/* Acciones */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Acciones</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Optimizar con AI</span>
                      <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">Ctrl + O</kbd>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Guardar prompt</span>
                      <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">Ctrl + S</kbd>
                    </div>
                  </div>
                </div>

                {/* Agregar Nodos */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Agregar Nodos</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Nueva instrucción</span>
                      <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">Ctrl + I</kbd>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Nueva condición</span>
                      <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">Ctrl + K</kbd>
                    </div>
                  </div>
                </div>

                {/* Edición */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Edición</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">Eliminar nodo seleccionado</span>
                      <div className="flex gap-2">
                        <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">Delete</kbd>
                        <span className="text-gray-400">o</span>
                        <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono">Backspace</kbd>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Nota para Mac */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Nota:</strong> En Mac, usa <kbd className="px-2 py-1 bg-white border border-blue-300 rounded text-xs font-mono">Cmd</kbd> en lugar de <kbd className="px-2 py-1 bg-white border border-blue-300 rounded text-xs font-mono">Ctrl</kbd>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
